<template>
  <b-modal
    id="privateBookingModal"
    ref="privateBookingModal"
    title="Private Booking"
    hide-footer
    no-close-on-backdrop
    size="lg"
    centered
    @shown="onShown"
    @hidden="onHidden"
  >
    <div class="private-booking-modal">
      <!-- Step 1: choose an event -->
      <div class="pb-section">
        <label class="pb-label">Select Event</label>

        <div v-if="loadingEvents" class="pb-muted">Loading events…</div>

        <div v-else-if="!events.length" class="pb-muted">
          No private booking events are configured.
        </div>

        <div v-else class="pb-events">
          <button
            v-for="ev in events"
            :key="ev.event_id"
            type="button"
            class="pb-event-btn"
            :class="{ active: selectedEventId === ev.event_id }"
            @click="selectEvent(ev)"
          >
            {{ ev.name }}
          </button>
        </div>
      </div>

      <!-- Step 2: configuration / confirmation -->
      <div v-if="selectedEventId" class="pb-section">
        <div v-if="loadingConfig" class="pb-muted">
          Checking availability…
        </div>

        <template v-else-if="config">
          <div v-if="!config.configured" class="pb-alert pb-alert-warn">
            {{ config.message || "No private booking configuration for this show." }}
          </div>

          <div v-else-if="!config.session_empty" class="pb-alert pb-alert-danger">
            {{
              config.message ||
              "Private booking is not allowed — this session already has booked or reserved seats."
            }}
          </div>

          <div v-else-if="!config.rate_card_assigned" class="pb-alert pb-alert-warn">
            No rate card is assigned for this configuration.
          </div>

          <div v-else class="pb-summary">
            <div class="pb-summary-row">
              <span>Seats to be booked</span>
              <strong>{{ config.seats_to_be_booked }}</strong>
            </div>
            <div class="pb-summary-row">
              <span>Price per seat</span>
              <strong>
                {{ formatAmount(config.unit_price) }} {{ currencyCode }}
              </strong>
            </div>
            <div class="pb-summary-row pb-summary-total">
              <span>Estimated total</span>
              <strong>
                {{ formatAmount(config.estimated_total) }} {{ currencyCode }}
              </strong>
            </div>
            <p class="pb-note">
              {{ config.seats_to_be_booked }} seat(s) will be auto-selected and
              held, then you'll proceed to checkout as a normal booking.
            </p>
          </div>
        </template>
      </div>

      <!-- Actions -->
      <div class="pb-actions">
        <button
          type="button"
          class="btn btn-outline-primary"
          :disabled="reserving"
          @click="close"
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!canReserve || reserving"
          @click="reserveAndProceed"
        >
          {{ reserving ? "Reserving…" : "Reserve & Proceed" }}
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import PosService from "@/services/PosService.js";

export default {
  name: "PrivateBookingModal",
  props: {
    cinemaId: { type: [String, Number], default: null },
    ssId: { type: [String, Number], default: null },
    showDetails: { type: Object, default: () => ({}) },
    reservationId: { type: [String, Number], default: null },
    currencyCode: { type: String, default: "" },
  },
  data() {
    return {
      events: [],
      loadingEvents: false,
      selectedEventId: null,
      config: null,
      loadingConfig: false,
      reserving: false,
    };
  },
  computed: {
    canReserve() {
      return !!(
        this.config &&
        this.config.configured &&
        this.config.session_empty &&
        this.config.rate_card_assigned &&
        Number(this.config.seats_to_be_booked) > 0
      );
    },
  },
  methods: {
    onShown() {
      this.resetState();
      this.fetchEvents();
    },
    onHidden() {
      this.resetState();
    },
    resetState() {
      this.selectedEventId = null;
      this.config = null;
      this.loadingConfig = false;
      this.reserving = false;
    },
    close() {
      this.$bvModal.hide("privateBookingModal");
    },
    formatAmount(value) {
      return parseFloat(value || 0).toFixed(2);
    },
    async fetchEvents() {
      this.loadingEvents = true;
      try {
        const { data } = await PosService.getPrivateBookingEvents();
        this.events = (data && data.data) || [];
      } catch (err) {
        this.$toasted.show("Failed to load private booking events");
        this.events = [];
      } finally {
        this.loadingEvents = false;
      }
    },
    async selectEvent(ev) {
      this.selectedEventId = ev.event_id;
      this.config = null;
      await this.fetchConfig();
    },
    async fetchConfig() {
      if (!this.selectedEventId) return;
      this.loadingConfig = true;
      try {
        const { data } = await PosService.getPrivateBookingConfig({
          cinema_id: this.cinemaId,
          session_id: this.ssId,
          pb_event_id: this.selectedEventId,
        });
        if (data && data.status) {
          this.config = data.data;
        } else {
          this.config = {
            configured: false,
            session_empty: true,
            message: (data && data.message) || "Unable to fetch configuration",
          };
        }
      } catch (err) {
        this.$toasted.show("Failed to fetch private booking configuration");
        this.config = {
          configured: false,
          session_empty: true,
          message: "Failed to fetch configuration",
        };
      } finally {
        this.loadingConfig = false;
      }
    },
    async reserveAndProceed() {
      if (!this.canReserve || this.reserving) return;
      this.reserving = true;
      try {
        const { data } = await PosService.reservePrivateBooking({
          cinema_id: this.cinemaId,
          session_id: this.ssId,
          pb_event_id: this.selectedEventId,
          reservation_id: this.reservationId,
        });
        if (data && data.status) {
          const payload = data.data || {};
          this.$bvModal.hide("privateBookingModal");
          this.$emit("reserved", {
            reservation_id: payload.reservation_id || this.reservationId,
            tt_id: payload.tt_id || [],
            seat_type_id: payload.seat_type_id || [],
          });
        } else {
          this.$toasted.show(
            (data && data.message) || "Unable to reserve private booking"
          );
        }
      } catch (err) {
        const msg =
          (err.response && err.response.data && err.response.data.message) ||
          "Unable to reserve private booking";
        this.$toasted.show(msg);
      } finally {
        this.reserving = false;
      }
    },
  },
};
</script>

<style scoped>
.private-booking-modal {
  padding: 4px 2px;
}
.pb-section {
  margin-bottom: 18px;
}
.pb-label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
}
.pb-muted {
  color: #888;
  font-size: 14px;
}
.pb-events {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.pb-event-btn {
  border: 1px solid #d0d0d0;
  background: #fff;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.pb-event-btn:hover {
  border-color: #2f6fed;
}
.pb-event-btn.active {
  border-color: #2f6fed;
  background: #2f6fed;
  color: #fff;
}
.pb-alert {
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 14px;
}
.pb-alert-warn {
  background: #fff6e5;
  color: #8a6d3b;
}
.pb-alert-danger {
  background: #fdecea;
  color: #b71c1c;
}
.pb-summary {
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 14px 16px;
}
.pb-summary-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
}
.pb-summary-total {
  border-top: 1px dashed #ddd;
  margin-top: 6px;
  padding-top: 10px;
  font-size: 16px;
}
.pb-note {
  margin: 10px 0 0;
  font-size: 12px;
  color: #888;
}
.pb-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
}
</style>



// WEBPACK FOOTER //
// src/components/Partials/PrivateBookingModal.vue